import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import api from "api";
import useNav from "core/hooks/useNav";
import { useStoreCards, useStoreDeposit } from "features/pay/store";
import { useStart } from "features/start/fabric";


// DEPOSIT
export const useMutateDeposit = () => {
	const deposit = useStoreDeposit();
	const cards = useStoreCards();
	const start = useStart();
	const nav = useNav();

	return useMutation({
		mutationKey: ["deposit"],
		mutationFn: () => {
			let body = { profileId: start.profile.id, tokens: deposit.token?.tokens, cardId: cards.method?.id };
			if (deposit.reload) body.reload = deposit.reload;

			return api.payment.deposit(body);
		},
		onSuccess: () => {
			const ssk = nav.keys.ssk;
			const cid = nav.keys.cid;

			if (ssk || cid) {
				const params = ssk ? `sub_sk=${ssk}` : `sub_cid=${cid}`;
				nav.open(`/unlock?${params}`); /* check if there is a ssk or content id */
			}
			else nav.open("/"); /* otherwise redirect to home page */
		}
	});
};

// CARDS
export const useQueryCards = () => {
	const store = useStoreCards();
	const start = useStart();
	const setup = useMutateCardSetup();

	return useQuery({
		queryKey: ["cards"],
		queryFn: async () => {
			const data = await api.card.all(start.profile.id);

			if (data.length === 0) await setup.mutateAsync();
			else store.setMethod(data.find(c => c.default));

			return data;
		}
	});
};

export const useMutateCardSetup = () => {
	const cards = useStoreCards();
	const start = useStart();

	return useMutation({
		mutationKey: ["cards-setup"],
		mutationFn: () => api.card.setup(start.profile.id),
		onMutate: () => cards.setAdding(true),
		onSuccess: (data) => cards.setIntent(data.intent)
	});
};

export const useMutateCardAdd = () => {
	const cards = useStoreCards();
	const queryClient = useQueryClient();
	const start = useStart();

	return useMutation({
		mutationKey: ["card-add"],
		mutationFn: (externalId) => api.card.add({ profileId: start.profile.id, externalId, default: cards.default }),
		onSuccess: (data) => {
			queryClient.setQueryData(["cards"], (state) => {
				if (data.default) {
					const cards = state.map(c => !c.default ? c : ({ ...c, default: false }));
					return ([...cards, data]);
				}
				else return ([...state, data]);
			});
			cards.setAdding(false);
			cards.setMethod(data);
			cards.toggleDefault();
		}
	});
};

export const useMutateCardDelete = () => {
	const queryClient = useQueryClient();
	const start = useStart();

	return useMutation({
		mutationKey: ["card-delete"],
		mutationFn: (card) => api.card.delete({ profileId: start.profile.id, id: card.id }),
		onSuccess: (_, card) => {
			queryClient.setQueryData(["cards"], (state) => state.filter(c => c.id != card.id));
		}
	});
};

export const useMutateCardDefault = () => {
	const cards = useStoreCards();
	const queryClient = useQueryClient();
	const start = useStart();

	return useMutation({
		mutationKey: ["card-default"],
		mutationFn: (card) => api.card.updateDefault({ profileId: start.profile.id, cardId: card.id }),
		onSuccess: (data) => {
			cards.setMethod(data.find(c => c.default));
			queryClient.setQueryData(["cards"], data);
		}
	});
};